import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { User, Trash2, Plus, Camera, UserCircle, Shirt } from 'lucide-react';
import PropTypes from 'prop-types';
import PhotoUploadModal from './PhotoUploadModal';
import { listUserPhotosFromS3, deleteUserPhotoFromS3 } from 'utils/s3UploadHelper';
import { getError } from 'utils/appHelpers';
import './VirtualTryOn.scss';

/**
 * Photo Manager Component
 * Allows users to manage their uploaded photos for virtual try-on
 * Can be accessed from user profile settings
 */
const PhotoManager = ({ className = '' }) => {
  const { t } = useTranslation();
  const [photos, setPhotos] = useState([]);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Photo type labels with Lucide icons (no emojis)
  const photoTypeLabels = {
    'full-body-front': { label: t('Full Body - Front'), Icon: User },
    'full-body-back': { label: t('Full Body - Back'), Icon: User },
    'head-only': { label: t('Head Only'), Icon: UserCircle },
    'upper-body': { label: t('Upper Body'), Icon: Shirt },
  };

  // Load photos from S3 on component mount
  useEffect(() => {
    // One-time migration: clear old format photos from localStorage
    const migrationKey = 'virtualTryOn_migrated_v2';
    if (!localStorage.getItem(migrationKey)) {
      localStorage.removeItem('virtualTryOnPhotos');
      localStorage.setItem(migrationKey, 'true');
    }

    loadPhotos();
  }, []);

  const loadPhotos = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Load from S3
      const s3Photos = await listUserPhotosFromS3();

      // Filter out generated results only (keep all user-uploaded photos)
      const validPhotos = s3Photos.filter(photo => {
        // Skip if missing photoPath
        if (!photo.photoPath) {
          console.warn('[PhotoManager] Skipping photo missing photoPath:', photo);
          return false;
        }

        // All photos with photoPath are valid (filtering already done in listUserPhotosFromS3)
        return true;
      });

      setPhotos(validPhotos);

      // Update localStorage cache
      localStorage.setItem('virtualTryOnPhotos', JSON.stringify(validPhotos));
    } catch (err) {
      console.error('Failed to load photos:', err);
      setError(getError(err));

      // Fallback to localStorage if S3 fails
      const storedPhotos = JSON.parse(localStorage.getItem('virtualTryOnPhotos') || '[]');
      setPhotos(storedPhotos);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhotoUpload = uploadedPhoto => {
    // Reload photos after upload
    loadPhotos();
  };

  const handleDeletePhoto = async photoId => {
    try {
      // Find the photo to get its S3 key
      const photo = photos.find(p => p.id === photoId);
      if (photo && photo.s3Key) {
        await deleteUserPhotoFromS3(photo.s3Key);
      }

      // Update local state
      const updatedPhotos = photos.filter(photo => photo.id !== photoId);
      setPhotos(updatedPhotos);
      localStorage.setItem('virtualTryOnPhotos', JSON.stringify(updatedPhotos));
    } catch (err) {
      console.error('Failed to delete photo:', err);
      setError(getError(err));
    }
  };
  const groupPhotosByType = () => {
    const grouped = {};
    photos.forEach(photo => {
      const type = photo.photoType || 'full-body-front';
      if (!grouped[type]) {
        grouped[type] = [];
      }
      grouped[type].push(photo);
    });
    return grouped;
  };
  const groupedPhotos = groupPhotosByType();

  return (
    <div className={`photo-manager ${className}`}>
      {/* Header */}
      <div className='photo-manager__header'>
        <div className='photo-manager__badge'>
          <Camera className='photo-manager__badge-icon' aria-hidden='true' />
          <span className='photo-manager__badge-text'>{t('VIRTUAL TRY-ON')}</span>
        </div>

        <h2 className='photo-manager__title'>
          {t('My')} <span className='photo-manager__title--gradient'>{t('Photos')}</span>
        </h2>

        <p className='photo-manager__description'>
          {t('Upload and manage photos in different poses for virtual try-on')}
        </p>

        <button className='photo-manager__cta' onClick={() => setIsUploadModalOpen(true)} disabled={isLoading}>
          <Plus className='photo-manager__cta-icon' aria-hidden='true' />
          <span>{t('Add Photo')}</span>
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className='photo-manager__error'>
          <p>{error}</p>
        </div>
      )}

      {/* Loading State */}
      {isLoading ? (
        <div className='photo-manager__loading'>
          <p>{t('Loading photos...')}</p>
        </div>
      ) : (
        <>
          {/* Empty State */}
          {photos.length === 0 ? (
            <div className='photo-manager__empty'>
              <div className='photo-manager__empty-icon-wrapper'>
                <Camera className='photo-manager__empty-icon' aria-hidden='true' />
              </div>
              <h3 className='photo-manager__empty-title'>{t('No photos yet')}</h3>
              <p className='photo-manager__empty-description'>
                {t('Upload your first photo to start trying on fashion items virtually')}
              </p>
              <button className='photo-manager__empty-cta' onClick={() => setIsUploadModalOpen(true)}>
                <Plus className='photo-manager__empty-cta-icon' aria-hidden='true' />
                <span>{t('Upload Photo')}</span>
              </button>
            </div>
          ) : (
            /* Photo Grid */ <div className='photo-manager__grid'>
              {Object.entries(groupedPhotos).map(([type, typePhotos]) => {
                const TypeIcon = photoTypeLabels[type]?.Icon || Camera;
                return (
                  <div key={type} className='photo-manager__section'>
                    {/* Section Header */}
                    <div className='photo-manager__section-header'>
                      <div className='photo-manager__section-icon-wrapper'>
                        <TypeIcon className='photo-manager__section-icon' size={20} aria-hidden='true' />
                      </div>
                      <h3 className='photo-manager__section-title'>{photoTypeLabels[type]?.label || t('Other')}</h3>
                      <span className='photo-manager__section-count'>{typePhotos.length}</span>
                    </div>

                    {/* Photos Grid */}
                    <div className='photo-manager__photos'>
                      {typePhotos.map(photo => (
                        <div key={photo.id} className='photo-manager__photo'>
                          <div className='photo-manager__photo-wrapper'>
                            <img
                              src={photo.url || photo.dataUrl}
                              alt={photoTypeLabels[type]?.label}
                              className='photo-manager__photo-img'
                            />
                            <button
                              className='photo-manager__photo-delete'
                              onClick={() => handleDeletePhoto(photo.id)}
                              aria-label={t('Delete photo')}
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Photo Upload Modal */}
      <PhotoUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onPhotoUpload={handlePhotoUpload}
      />
    </div>
  );
};
PhotoManager.propTypes = { className: PropTypes.string };
export default PhotoManager;
